import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
} from 'components/icons';
import { closeDialog, openDialog } from 'components/dialog/modalSlice';
import DangerDialog from 'components/dialog/templates/DangerDialog';
import { OwnerTypes } from 'entities/ownership';
import { useAppDispatch } from 'hooks';
import Button from 'components/Button';

import { deleteOwnerBusiness, deleteOwnerPerson } from '../thunks';
import { removeOwner } from '../ownershipSlice';
import { isOwnerComplete } from '../utils';
import IndividualForm from './IndividualForm';
import EntityForm from './EntityForm';
import OwnerRadio from './OwnerRadio';

interface OwnerProps {
  data: any;
  index: number;
  showErrors: boolean;
  permissions: any;
}

const Owner: React.FC<OwnerProps> = ({
  data,
  index,
  showErrors,
  permissions,
}) => {
  const { t } = useTranslation('ownership');
  const dispatch = useAppDispatch();

  const hasId = Boolean(
    data.businessesOwnersPersonsId || data.businessesOwnersBusinessesId,
  );

  const [entityType, setEntityType] = useState<number>(data.type);
  const [isEditing, setIsEditing] = useState<boolean>(!hasId);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const name =
    data.type === OwnerTypes.person
      ? `${data.givenName1 || ''} ${data.surnameFirst || ''}`.trim()
      : data.businessLegalName;

  const handleExpand = () => {
    setEntityType(data.type);
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (hasId) {
      setEntityType(data.type);
      setIsEditing(false);
    } else {
      dispatch(removeOwner(index));
    }
  };

  const handleSaved = () => {
    setIsEditing(false);
  };

  const closeConfirmationDialog = () => {
    dispatch(closeDialog());
  };

  const handleRemove = () => {
    dispatch(
      openDialog({
        maxWidth: '512px',
        onClose: closeConfirmationDialog,
        onCancel: closeConfirmationDialog,
        content: (
          <DangerDialog
            title={t('common:confirmationText')}
            buttonText={t('common:remove')}
            onCancel={closeConfirmationDialog}
            onConfirm={async () => {
              closeConfirmationDialog();

              setIsDeleting(true);

              await dispatch(
                data.type === OwnerTypes.business
                  ? deleteOwnerBusiness(data.businessesOwnersBusinessesId)
                  : deleteOwnerPerson(data.businessesOwnersPersonsId),
              );
            }}
          />
        ),
      }),
    );
  };

  return (
    <div className="bg-gray-50 rounded-lg p-6 my-5">
      {isEditing ? (
        <>
          <div className="flex items-center justify-between">
            <p className="font-semibold">
              {t('ownership:ownerNumber', { number: index + 1 })}
            </p>
            {hasId && (
              <button
                className="p-2 cursor-pointer rounded-10 border border-gray-200 hover:bg-gray-100"
                onClick={handleCancel}
              >
                <ChevronUpIcon />
              </button>
            )}
          </div>

          <OwnerRadio
            idPrefix={`entityType-${index}`}
            label={t('ownership:fields.entityType.label')}
            options={[
              { value: OwnerTypes.person, name: t('ownership:individualTab') },
              { value: OwnerTypes.business, name: t('ownership:entityTab') },
            ]}
            value={entityType}
            onChange={(value) => setEntityType(value)}
            disabled={!permissions.edit}
            className="mt-6"
          />

          <IndividualForm
            isOpen={entityType === OwnerTypes.person}
            ownerData={data}
            number={index + 1}
            onSaved={handleSaved}
            onCancel={handleCancel}
          />
          <EntityForm
            isOpen={entityType === OwnerTypes.business}
            ownerData={data}
            number={index + 1}
            onSaved={handleSaved}
            onCancel={handleCancel}
          />
        </>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="font-semibold">{name}</span>
            <span className="ml-3 text-xs text-gray-500 bg-gray-200 rounded-full px-2 py-0.5">
              {t('ownership:ownerShare', { value: data.ownershipPercentage })}
            </span>
            {showErrors && !isOwnerComplete(data) && (
              <ExclamationCircleIcon className="ml-2 text-yellow-500" />
            )}
          </div>
          <div className="flex items-center">
            {permissions.edit && (
              <Button
                variant="text"
                onClick={handleRemove}
                loading={isDeleting}
              >
                {t('common:remove')}
              </Button>
            )}
            <button
              className="p-2 ml-3 cursor-pointer rounded-10 border border-gray-200 hover:bg-gray-100"
              onClick={handleExpand}
            >
              <ChevronDownIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Owner;
